import { Icon, PageLayout } from 'components/common';
import useUnderage from 'hooks/useUnderage';
import { useCallback, useEffect, useRef, useState } from 'react';
import { UNDERAGE_CONSTANTS, handleCancelAndClose } from 'utils/underageUtils';

type Step = 'guide' | 'prepare';
type DetectionState = 'waiting' | 'success' | 'retry';

const PageUnderAgeVerification = () => {
  const [step, setStep] = useState<Step>('guide');
  const [detectionState, setDetectionState] = useState<DetectionState>('waiting');
  const [isVisible, setIsVisible] = useState(false);
  const [textOpacity, setTextOpacity] = useState(0);
  const { setCapturedFile, setUploading, createdAt, reset, uploadImage, setFromVerification } = useUnderage();
  const detectionTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startDetectionRef = useRef<(() => void) | null>(null);
  const isPageVisible = useRef(true);

  const startGuideToPrepare = () => {
    setIsVisible(true);

    setTimeout(() => {
      setTextOpacity(1);
    }, 100);

    setTimeout(() => {
      setTextOpacity(0);
      setTimeout(() => setStep('prepare'), UNDERAGE_CONSTANTS.TIMING.FADE_DURATION);
    }, UNDERAGE_CONSTANTS.TIMING.GUIDE_WAIT);
  };

  const stopDetection = useCallback(() => {
    if (detectionTimer.current) {
      clearTimeout(detectionTimer.current);
      detectionTimer.current = null;
    }
    if (retryTimer.current) {
      clearTimeout(retryTimer.current);
      retryTimer.current = null;
    }
    window.FaceDetector?.setFaceDetect(0, false, () => {});
  }, []);

  const handleBack = useCallback(() => {
    handleCancelAndClose(() => {
      stopDetection();
      setStep('guide');
      setDetectionState('waiting');
      setIsVisible(false);
      setTextOpacity(0);
      reset();
    });
  }, [stopDetection, reset]);

  const captureAndSave = useCallback(async () => {
    if (!VideoUtil?.requestCapture) {
      setCapturedFile({ base64: '', timestamp: Date.now() });
      setTimeout(() => Transition.showPage(Def.PAGE_UNDERAGE_FORM), 500);
      return;
    }

    VideoUtil.requestCapture(
      VideoUtil.MYVIDEO,
      async (obj: { normal: string }) => {
        if (obj?.normal) {
          setCapturedFile({ base64: obj.normal, timestamp: Date.now() });

          try {
            setUploading(true);
            const filename = `face_${createdAt}.jpg`;
            await uploadImage(obj.normal, filename, 'face');
            stopDetection();
            setTimeout(() => {
              setUploading(false);
              setFromVerification(true);
              Transition.showPage(Def.PAGE_UNDERAGE_FORM);
            }, 500);
          } catch (error) {
            // 업로드 실패 시 취소 플로우로 보내지 않고 얼굴 재인식으로 자연스럽게 재시도
            const hMainContainer = document.getElementById('hMainContainer');
            if (hMainContainer) {
              CommonUI.showToast(hMainContainer, { text: brsc.try_later, duration: 2000, position: 'top' });
            }
            setDetectionState('retry');
            retryTimer.current = setTimeout(() => startDetectionRef.current?.(), 1000);
          } finally {
            setUploading(false);
          }
        } else {
          setDetectionState('retry');
          retryTimer.current = setTimeout(() => startDetectionRef.current?.(), 1000);
        }
      },
      360,
      0,
      0,
      0,
      false,
      50
    );
  }, [setCapturedFile, setUploading, stopDetection, createdAt, uploadImage, setFromVerification]);

  const faceDetectCallback = useCallback(
    (id: number) => {
      if (id >= 0 && isPageVisible.current) {
        setDetectionState('success');

        if (detectionTimer.current) {
          clearTimeout(detectionTimer.current);
          detectionTimer.current = null;
        }

        captureAndSave();
      }
    },
    [captureAndSave]
  );

  const startDetection = useCallback(() => {
    setDetectionState('waiting');

    if (detectionTimer.current) {
      clearTimeout(detectionTimer.current);
    }

    window.FaceDetector?.setFaceDetect(0, true, faceDetectCallback, 500);

    detectionTimer.current = setTimeout(() => {
      window.FaceDetector?.setFaceDetect(0, false, () => {});
      setDetectionState('retry');
      retryTimer.current = setTimeout(() => startDetectionRef.current?.(), 1000);
    }, UNDERAGE_CONSTANTS.TIMING.DETECTION_TIMEOUT);
  }, [faceDetectCallback]);

  startDetectionRef.current = startDetection;

  useEffect(() => {
    window.UnderagePageBridge?.setVerificationBackCallback(handleBack);
    return () => {
      window.UnderagePageBridge?.setVerificationBackCallback(null);
    };
  }, [handleBack]);

  useEffect(() => {
    VideoUtil.setVisible(VideoUtil.MYVIDEO, true);
    // 검증 페이지 진입 시 밴 모달을 화면에서만 제거(부수효과 X). 검색종료·로그아웃 종료 경로가 아니므로 hideAndExit가 아닌 hide 사용.
    RestrictionModal.hide();
    MainUI.showMainUI('hide');
    startGuideToPrepare();
    isPageVisible.current = true;

    const handleVisibilityChange = () => {
      isPageVisible.current = !document.hidden;
      if (document.hidden) {
        stopDetection();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isPageVisible.current = false;
      stopDetection();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [stopDetection]);

  useEffect(() => {
    if (step === 'prepare') {
      setTimeout(() => {
        startDetection();
      }, 1000);
    }
  }, [step, startDetection]);

  return (
    <PageLayout backgroundColor="transparent">
      <div className="pointer-events-none absolute inset-0 z-0">
        <svg className="h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <mask id="faceMask">
              <rect width="100" height="100" fill="white" />
              <ellipse cx="50" cy="45" rx="45" ry="35" fill="black" />
            </mask>
          </defs>
          <rect width="100" height="100" fill="rgba(0,0,0,0.7)" mask="url(#faceMask)" />
        </svg>
      </div>

      <div className="fixed inset-0 z-10 bg-white transition-opacity duration-300" style={{ opacity: step === 'guide' ? 1 : 0 }} />

      <div className="relative z-20 h-full w-full">
        {step === 'guide' && (
          <div className="fixed inset-0 flex items-center justify-center transition-opacity duration-300" style={{ opacity: isVisible ? 1 : 0 }}>
            <p className="px-4 text-center typography-title-lg text-black transition-opacity duration-500" style={{ opacity: textOpacity }}>
              {brsc.age_verification_camera_1}
            </p>
          </div>
        )}

        {step === 'prepare' && (
          <div className="relative z-10 flex h-full w-full flex-1 flex-col items-center justify-center">
            <Icon name="icon/ic_close.svg" onClick={handleBack} className="absolute left-4 top-16 z-20" color="filter-base-ic-white" />
            <div className="absolute bottom-20 z-20 px-4 text-center">
              <p className="typography-title-md font-medium text-white">{detectionState === 'success' ? brsc.age_verification_toast : brsc.age_verification_camera_2}</p>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default PageUnderAgeVerification;
